import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useAuth } from '../../contexts/AuthProvider';

const categoryBadge = (label) => (
  <span className="badge bg-secondary text-light">{label || '-'}</span>
);

const Impartiality = () => {
  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canImpartiality = (key) => can(key, 'impartiality');

  const [searchTerm, setSearchTerm] = useState('');
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [impartialityName, setImpartialityName] = useState('');
  const [serviceSelect, setServiceSelect] = useState('');
  const [impDescription, setImpDescription] = useState('');
  const [firstQualification, setFirstQualification] = useState('');
  const [secondQualification, setSecondQualification] = useState('');
  const [servicesOptions, setServicesOptions] = useState([]);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [rulePendingDelete, setRulePendingDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  const qualificationLabelMap = useMemo(() => {
    const map = new Map();
    qualificationOptions.forEach((qualification) => {
      map.set(String(qualification.id), qualification.qualification);
    });
    return map;
  }, [qualificationOptions]);

  const serviceLabelMap = useMemo(() => {
    const map = new Map();
    servicesOptions.forEach((service) => {
      map.set(String(service.id), service.name);
    });
    return map;
  }, [servicesOptions]);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setImpartialityName('');
    setServiceSelect('');
    setImpDescription('');
    setFirstQualification('');
    setSecondQualification('');
  }, []);

  const hideModal = useCallback(() => {
    const element = document.getElementById('modal-create-impartiality');
    if (!element) return;
    const modal = window.bootstrap?.Modal.getInstance(element) || new window.bootstrap.Modal(element);
    modal.hide();
  }, []);

  const openModal = useCallback((rule = null) => {
    if (rule) {
      setEditingId(rule.id);
      setImpartialityName(rule.name || '');
      setServiceSelect(rule.serviceId ? String(rule.serviceId) : '');
      setImpDescription(rule.description || '');
      setFirstQualification(rule.firstQualificationId ? String(rule.firstQualificationId) : '');
      setSecondQualification(rule.secondQualificationId ? String(rule.secondQualificationId) : '');
    } else {
      resetForm();
    }

    const element = document.getElementById('modal-create-impartiality');
    if (!element) return;
    const modal = new window.bootstrap.Modal(element, { backdrop: 'static', keyboard: true });
    modal.show();
  }, [resetForm]);

  const fetchServices = useCallback(async () => {
    try {
      const response = await api.get('/md/company-service-page', { params: { page: 0, size: 1000 } });
      const serviceItems = response?.data?.content || response?.data?.items || response?.data?.data || [];
      setServicesOptions(Array.isArray(serviceItems) ? serviceItems : []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      setServicesOptions([]);
    }
  }, []);

  const fetchQualifications = useCallback(async () => {
    try {
      const response = await api.get('/audit/personnel-qualifications');
      setQualificationOptions(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch qualifications:', error);
      setQualificationOptions([]);
    }
  }, []);

  const fetchRules = useCallback(async (searchValue = '') => {
    setLoading(true);
    try {
      const response = await api.get('/md/impartiality-rules', { params: { search: searchValue } });
      const items = Array.isArray(response?.data)
        ? response.data
        : response?.data?.content || response?.data?.items || response?.data?.data || [];
      setRules(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Failed to fetch impartiality rules:', error);
      setRules([]);
      setFailMessage(error?.response?.data?.message || 'Failed to load impartiality rules.');
      setShowFailModal(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchServices();
    fetchQualifications();
  }, [fetchQualifications, fetchRules, fetchServices]);

  useEffect(() => {
    const element = document.getElementById('modal-create-impartiality');
    if (!element) return undefined;

    const handleHidden = () => resetForm();
    element.addEventListener('hidden.bs.modal', handleHidden);
    return () => element.removeEventListener('hidden.bs.modal', handleHidden);
  }, [resetForm]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    fetchRules(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!impartialityName.trim() || !serviceSelect || !firstQualification || !secondQualification) {
      setFailMessage('Please fill all required fields before saving the impartiality rule.');
      setShowFailModal(true);
      return;
    }

    if (firstQualification === secondQualification) {
      setFailMessage('The conflicting qualification must be different from the first qualification.');
      setShowFailModal(true);
      return;
    }

    const firstQualificationLabel = qualificationLabelMap.get(String(firstQualification)) || '';
    const secondQualificationLabel = qualificationLabelMap.get(String(secondQualification)) || '';
    const serviceLabel = serviceLabelMap.get(String(serviceSelect)) || '';

    const payload = {
      name: impartialityName.trim(),
      description: impDescription.trim(),
      serviceId: Number(serviceSelect),
      firstQualificationId: Number(firstQualification),
      secondQualificationId: Number(secondQualification),
      standard: serviceLabel || undefined,
      firstRole: firstQualificationLabel || undefined,
      secondRole: secondQualificationLabel || undefined
    };

    try {
      setIsSaving(true);
      if (editingId) {
        await api.put(`/md/impartiality-rules/${editingId}`, payload);
        setSuccessMessage('Impartiality rule updated successfully.');
      } else {
        await api.post('/md/impartiality-rules', payload);
        setSuccessMessage('Impartiality rule created successfully.');
      }
      hideModal();
      setShowSuccessModal(true);
      fetchRules(searchTerm);
    } catch (error) {
      console.error('Failed to save impartiality rule:', error);
      setFailMessage(error?.response?.data?.message || 'Failed to save impartiality rule.');
      setShowFailModal(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (rule) => {
    if (!canImpartiality('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    setRulePendingDelete(rule);
    setShowDeleteConfirmation(true);
  };

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div>
                <h2 className="page-title mb-1">Impartiality</h2>
                <p className="text-muted mb-0">Manage impartiality rules and conflict of interest detection in audit assignments.</p>
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={() => openModal()}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Create Impartiality
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="mb-4 d-flex gap-2">
                  <input className="form-control" placeholder="Search ..." value={searchTerm} onChange={handleSearchChange} />
                </div>

                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-hover">
                    <thead>
                      <tr>
                        <th>Impartiality Name</th>
                        <th>Description</th>
                        <th>Qualifications</th>
                        <th>Service</th>
                        <th className="w-1">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : rules.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            No impartiality rules found
                          </td>
                        </tr>
                      ) : (
                        rules.map((rule) => {
                          const conductingRole = `${qualificationLabelMap.get(String(rule.firstQualificationId)) || rule.firstQualificationId || '-'} / ${qualificationLabelMap.get(String(rule.secondQualificationId)) || rule.secondQualificationId || '-'}`;
                          const serviceLabel = serviceLabelMap.get(String(rule.serviceId)) || rule.serviceName || rule.standard || rule.serviceId;

                          return (
                            <tr key={rule.id}>
                              <td><strong>{rule.name}</strong></td>
                              <td className="text-muted small">{rule.description || '-'}</td>
                              <td>
                                <span className="badge bg-light text-dark">{conductingRole}</span>
                              </td>
                              <td>{categoryBadge(serviceLabel)}</td>
                              <td>
                                <div className="btn-list">
                                  <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openModal(rule)}>
                                    Edit
                                  </button>
                                  <button className="btn btn-sm btn-icon btn-ghost-danger" onClick={() => handleDelete(rule)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                  <div className="small text-muted">Showing {rules.length} impartiality rule{rules.length === 1 ? '' : 's'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal modal-blur fade" id="modal-create-impartiality" tabIndex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Impartiality Rule' : 'Create Impartiality Rule'}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <p className="small text-muted">Define an impartiality rule to detect conflicts of interest in audit assignments.</p>

                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">Impartiality Name</label>
                    <input
                      className="form-control"
                      value={impartialityName}
                      onChange={(event) => setImpartialityName(event.target.value)}
                      placeholder="e.g. Auditor - Technical Reviewer Conflict"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Service</label>
                    <select className="form-select" value={serviceSelect} onChange={(event) => setServiceSelect(event.target.value)}>
                      <option value="">-- Select Service --</option>
                      {servicesOptions.map((service) => (
                        <option key={service.id} value={service.id}>{service.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Qualification (First)</label>
                    <select className="form-select" value={firstQualification} onChange={(event) => setFirstQualification(event.target.value)}>
                      <option value="">-- Select Qualification --</option>
                      {qualificationOptions.map((qualification) => (
                        <option key={qualification.id} value={qualification.id}>{qualification.qualification}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Qualification (Conflicting)</label>
                    <select className="form-select" value={secondQualification} onChange={(event) => setSecondQualification(event.target.value)}>
                      <option value="">-- Select Qualification --</option>
                      {qualificationOptions.map((qualification) => (
                        <option key={qualification.id} value={qualification.id}>{qualification.qualification}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={impDescription}
                      onChange={(event) => setImpDescription(event.target.value)}
                      placeholder="Describe the impartiality rule"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" className="btn btn-success" disabled={isSaving}>
                  {isSaving ? 'Saving...' : (editingId ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setRulePendingDelete(null);
        }}
        onConfirm={async () => {
          if (!rulePendingDelete) {
            setShowDeleteConfirmation(false);
            return;
          }

          try {
            await api.delete(`/md/impartiality-rules/${rulePendingDelete.id}`);
            setShowDeleteConfirmation(false);
            setRulePendingDelete(null);
            setSuccessMessage('Impartiality rule deleted successfully.');
            setShowSuccessModal(true);
            fetchRules(searchTerm);
          } catch (error) {
            console.error('Failed to delete impartiality rule:', error);
            setShowDeleteConfirmation(false);
            setFailMessage(error?.response?.data?.message || 'Failed to delete impartiality rule.');
            setShowFailModal(true);
          }
        }}
        title="Delete Impartiality Rule"
        message={`Are you sure you want to delete "${rulePendingDelete?.name || 'this impartiality rule'}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default Impartiality;
